import React, { Children, Component, Fragment, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import { Alignment, Placement } from '@philips-internal/oswald-ui-constants';
import { DocumentContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { arrayOf, bool, element, expression, number, object, oneOf, oneOfType } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, getBestPosition, isContentfulString, isDefined, isEnumValid, seizeAndForwardChildrenEvents } from '@philips-internal/oswald-ui-utils';
import { TooltipTrigger } from './tooltip-constants.js';
import './tooltip.css';
class Tooltip extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            open: false,
        };
        this.isValidTrigger = (triggerType) => {
            const { triggers } = this.props;
            return Array.isArray(triggers) ? triggers.includes(triggerType) : triggers === triggerType;
        };
        this.onClick = () => {
            this.setState(({ open }) => ({ open: !open }));
        };
        this.onMouseEnter = () => {
            if (this.isValidTrigger(TooltipTrigger.HOVER)) {
                return this.setState({ open: true }, this.bindScrollEvent);
            }
            const { hostElement } = this.state;
            if (!hostElement) {
                return;
            }
            const { offsetWidth, scrollWidth } = hostElement;
            this.setState({ open: scrollWidth > offsetWidth }, this.bindScrollEvent);
        };
        this.onFocus = () => {
            if (!this.isValidTrigger(TooltipTrigger.FOCUS)) {
                return;
            }
            return this.setState({ open: true }, this.bindScrollEvent);
        };
        this.onBlur = () => {
            if (!this.isValidTrigger(TooltipTrigger.FOCUS)) {
                return;
            }
            this.setState({ open: false });
        };
        this.onMouseLeave = () => {
            if (this.isValidTrigger(TooltipTrigger.FOCUS)) {
                return;
            }
            this.setState({ open: false });
        };
        this.hostRef = (element) => {
            if (element) {
                this.setState({ hostElement: element });
            }
        };
        this.guestRef = (element) => {
            if (element) {
                this.setState({ guestElement: element });
            }
        };
        this.closeTooltip = () => {
            this.setState({ open: false }, this.unbindScrollEvent);
        };
        this.bindScrollEvent = () => {
            const { document } = this.context;
            document.addEventListener('scroll', this.closeTooltip, true);
        };
        this.unbindScrollEvent = () => {
            const { document } = this.context;
            document.removeEventListener('scroll', this.closeTooltip, true);
        };
    }
    getClassName(placement) {
        const { multiline } = this.props;
        return classNames({
            'c-tooltip': true,
            [`c-tooltip--${placement}`]: isEnumValid(Placement, placement),
            'c-tooltip--multiline': multiline,
        });
    }
    renderTarget() {
        const { children } = this.props;
        const [target] = Children.toArray(children);
        if (!isValidElement(target)) {
            return null;
        }
        const targetForwardEvents = [
            ['onFocus', this.onFocus],
            ['onBlur', this.onBlur],
        ];
        const targetProps = {
            onMouseEnter: this.onMouseEnter,
            onMouseLeave: this.onMouseLeave,
            ref: this.hostRef,
        };
        return seizeAndForwardChildrenEvents(target, targetForwardEvents, targetProps);
    }
    renderTooltip() {
        const { value, placement, alignment, showArrow, boundaryElement, offsetTop, offsetLeft, zIndex } = this.props;
        const { hostElement, guestElement } = this.state;
        const { document, window } = this.context;
        if (!isDefined(value) || !isContentfulString(value)) {
            return null;
        }
        const best = getBestPosition({
            alignment,
            arrowSize: showArrow ? 8 : 0,
            boundaryElement,
            guestElement,
            hostElement,
            offset: {
                bottom: 0,
                height: 0,
                left: offsetLeft,
                right: 0,
                top: offsetTop,
                width: 0,
            },
            placement,
            window,
        });
        const tooltip = (React.createElement("div", { className: this.getClassName(best.placement), ref: this.guestRef, style: { ...best.guest, zIndex } },
            showArrow && React.createElement("div", { className: "c-tooltip__arrow", style: { ...best.arrow } }),
            React.createElement("div", { className: "c-tooltip__content" },
                " ",
                MessageService.translate(String(value)),
                " ")));
        const tooltipManager = document.querySelector('.c-application__managers .c-tooltip-manager');
        return createPortal(tooltip, tooltipManager ?? document.body);
    }
    render() {
        const { open } = this.state;
        return (React.createElement(Fragment, null,
            open ? this.renderTooltip() : null,
            this.renderTarget()));
    }
}
Tooltip.contextType = DocumentContext;
Tooltip.propTypes = {
    alignment: oneOf(enumValues(Alignment)),
    boundaryElement: object,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    multiline: bool,
    offsetLeft: number,
    offsetTop: number,
    placement: oneOf(enumValues(Placement)),
    showArrow: bool,
    triggers: arrayOf(oneOf(enumValues(TooltipTrigger))),
    value: oneOfType([expression, number]),
    zIndex: number,
};
Tooltip.defaultProps = {
    alignment: Alignment.CENTER,
    multiline: false,
    offsetLeft: 0,
    offsetTop: 0,
    placement: Placement.TOP,
    showArrow: true,
    triggers: [TooltipTrigger.HOVER],
};
export { Tooltip, };
//# sourceMappingURL=tooltip.js.map